import React from "react";
import { Card, CardContent, Stack, Typography, Box, Chip, IconButton, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { enterpriseChartTheme } from "./chartTheme";

const ChartCard = ({ title, description, meta, children, onEdit, onDelete, onRefresh, isActive }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 3,
        border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: isDark
          ? "0 10px 30px rgba(0, 0, 0, 0.4)"
          : "0 10px 30px rgba(15, 23, 42, 0.08)",
        background: isDark
          ? "linear-gradient(180deg, #1e2536 0%, #171d2c 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isDark
            ? "0 16px 34px rgba(0, 0, 0, 0.5)"
            : "0 16px 34px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15, 23, 42, 0.06)",
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box minWidth={0} flex={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
              {description || "No description provided"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip label={isActive ? "Active" : "Inactive"} size="small" color={isActive ? "success" : "default"} />
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 2.25, pt: 1.75, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {meta?.modelName || "Chart"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta?.lastUpdated || "Updated recently"}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
      </CardContent>

      <Box
        sx={{
          px: 2.25,
          py: 1.25,
          borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15, 23, 42, 0.06)",
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.9)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {meta?.subtitle || "Enterprise analytics"}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {onRefresh && (
              <IconButton size="small" onClick={onRefresh} aria-label="refresh chart">
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
            {onEdit && (
              <IconButton size="small" onClick={onEdit} aria-label="edit chart">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton size="small" onClick={onDelete} aria-label="delete chart" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};

export default ChartCard;